import React, { useState, useEffect } from 'react';
import { kioskApi } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Umbrella, Plus, Clock, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const TIPO_LABEL = {
  vacaciones: 'Vacaciones',
  baja_medica: 'Baja médica',
  permiso_retribuido: 'Permiso retribuido',
  ausencia_justificada: 'Ausencia justificada',
  ausencia_injustificada: 'Ausencia injustificada',
  otro: 'Otro',
};

const ESTADO_META = {
  pendiente: { label: 'Pendiente', icon: Clock, box: 'border-amber-300 bg-amber-50', text: 'text-amber-700' },
  aprobada: { label: 'Aprobada', icon: CheckCircle2, box: 'border-emerald-300 bg-emerald-50', text: 'text-emerald-700' },
  rechazada: { label: 'Rechazada', icon: XCircle, box: 'border-red-300 bg-red-50', text: 'text-red-700' },
};

const KioskAusencias = ({ seleccionado, pin, onVolver }) => {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modo, setModo] = useState('lista');
  const [form, setForm] = useState({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
  const [enviando, setEnviando] = useState(false);
  const [saldo, setSaldo] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const [aus, sal] = await Promise.all([
        kioskApi.post('/kiosk/ausencias/mias', { user_id: seleccionado.id, pin }),
        kioskApi.post('/kiosk/vacaciones/saldo', { user_id: seleccionado.id, pin }).catch(() => ({ data: null })),
      ]);
      setLista(aus.data.ausencias || []);
      setSaldo(sal.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar');
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.fecha_inicio || !form.fecha_fin) { toast.error('Indica las fechas'); return; }
    if (form.fecha_fin < form.fecha_inicio) { toast.error('La fecha "Hasta" no puede ser anterior a "Desde"'); return; }
    setEnviando(true);
    try {
      await kioskApi.post('/kiosk/ausencias/solicitar', { user_id: seleccionado.id, pin, ...form });
      toast.success('Solicitud enviada. Tu responsable la revisará y recibirás email.');
      setForm({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
      setModo('lista');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo enviar'); }
    finally { setEnviando(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-100 p-6" data-testid="kiosk-ausencias-screen">
      <button onClick={onVolver} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900" data-testid="kiosk-ausencias-back">
        <ArrowLeft className="h-5 w-5" /> Volver
      </button>
      <div className="max-w-xl mx-auto pt-4">
        <div className="text-center mb-6">
          <Umbrella className="h-10 w-10 mx-auto mb-2 text-zinc-900" />
          <h2 className="font-heading text-2xl font-bold text-zinc-900">Mis ausencias</h2>
          <p className="text-zinc-500 text-sm mt-1">{seleccionado.full_name}</p>
        </div>

        {modo === 'lista' && (
          <>
            {saldo && (
              <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4" data-testid="kiosk-saldo-vacaciones">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                  Vacaciones {saldo.anio} · {saldo.politica?.tipo_computo === 'naturales' ? 'días naturales' : 'días laborables'}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center flex-1">
                    <div className="text-3xl font-bold text-zinc-900 tabular-nums" data-testid="kiosk-saldo-disponibles">{saldo.disponibles}</div>
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700">Disponibles</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-xl font-semibold text-zinc-600 tabular-nums">{saldo.consumidos}</div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">Disfrutadas</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-xl font-semibold text-amber-700 tabular-nums">{saldo.pendientes}</div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">Pendientes</div>
                  </div>
                </div>
              </div>
            )}
            <Button onClick={() => setModo('nueva')} data-testid="kiosk-nueva-ausencia-btn" className="w-full h-14 bg-zinc-900 hover:bg-zinc-800 text-lg gap-2 mb-4">
              <Plus className="h-5 w-5" /> Solicitar ausencia
            </Button>

            {cargando ? (
              <div className="text-center text-zinc-400 py-8">Cargando...</div>
            ) : lista.length === 0 ? (
              <div className="text-center text-zinc-500 bg-white border border-zinc-200 rounded-xl py-8" data-testid="kiosk-sin-ausencias">
                Aún no tienes solicitudes. Pulsa arriba para crear una.
              </div>
            ) : (
              <div className="space-y-2" data-testid="kiosk-ausencias-lista">
                {lista.map((a) => {
                  const meta = ESTADO_META[a.estado] || ESTADO_META.pendiente;
                  const Icon = meta.icon;
                  return (
                    <div key={a.id} className={`rounded-xl border-2 ${meta.box} px-4 py-3`} data-testid={`kiosk-ausencia-${a.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className={`h-4 w-4 ${meta.text}`} />
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
                        <span className="ml-auto text-sm text-zinc-700 font-semibold">{TIPO_LABEL[a.tipo] || a.tipo}</span>
                      </div>
                      <div className="text-zinc-900 text-base tabular-nums">
                        {new Date(a.fecha_inicio).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                        {a.fecha_inicio !== a.fecha_fin && ' → ' + new Date(a.fecha_fin).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                      </div>
                      {a.motivo && <div className="text-zinc-500 text-xs mt-1">{a.motivo}</div>}
                      {a.comentario_admin && (
                        <div className="mt-2 text-xs bg-white/70 rounded px-2 py-1.5 text-zinc-700">
                          <span className="font-semibold">Nota del responsable:</span> {a.comentario_admin}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {modo === 'nueva' && (
          <form onSubmit={enviar} className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="kiosk-form-ausencia">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="h-12" data-testid="kiosk-ausencia-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desde</Label>
                <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} required className="h-12" data-testid="kiosk-ausencia-desde" />
              </div>
              <div className="space-y-1.5">
                <Label>Hasta</Label>
                <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} required className="h-12" data-testid="kiosk-ausencia-hasta" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo (opcional)</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} maxLength={500} placeholder="Ej. viaje familiar, cita médica..." className="h-12" data-testid="kiosk-ausencia-motivo" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setModo('lista')} className="flex-1 h-12">Cancelar</Button>
              <Button type="submit" disabled={enviando} className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800" data-testid="kiosk-enviar-solicitud-btn">
                {enviando ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default KioskAusencias;
